import React, { useState, useMemo } from 'react';
import { T } from '../shared/constants';
import { ItemFrame, Instruction, CheckButton, ResultCard, partColors, usePointerDrag, DragGhost } from './ItemChrome';
import { buildStudentAnswer, isCorrectResponse, feedbackFor, seededShuffle, partMarks } from './naplanItems';

function moveItem(list, from, to) {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

const smallBtn = disabled => ({
  width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface,
  color: disabled ? T.border : T.textMid, cursor: disabled ? "default" : "pointer", fontSize: 14, fontWeight: 700,
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0,
});

/** `order` — put items in the correct order. Response: array of item texts. */
export default function OrderRenderer({ question, studentAnswer, state, showExplanation, onSubmit, onAskLumi, onExplainVideo }) {
  const isAnswered = studentAnswer != null;
  const items = question.items || [];
  const initial = useMemo(() => seededShuffle(items, question.id), [question.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [draft, setDraft] = useState(initial);
  const [picked, setPicked] = useState(null);
  const list = isAnswered && Array.isArray(studentAnswer.rawAnswer) ? studentAnswer.rawAnswer : draft;
  const isCorrect = isAnswered ? (typeof studentAnswer.isCorrect === 'boolean' ? studentAnswer.isCorrect : isCorrectResponse(question, list)) : false;
  const marks = isAnswered ? partMarks(question, list) : [];

  const { drag, start, consumeClick } = usePointerDrag({
    disabled: isAnswered,
    onDrop: (text, key) => {
      if (!key.startsWith('pos:')) return;
      setDraft(d => moveItem(d, d.indexOf(text), Number(key.slice(4))));
      setPicked(null);
    },
  });

  function tapRow(text) {
    if (isAnswered || consumeClick(text)) return;
    if (picked === null) { setPicked(text); return; }
    if (picked === text) { setPicked(null); return; }
    setDraft(d => moveItem(d, d.indexOf(picked), d.indexOf(text)));
    setPicked(null);
  }

  function nudge(i, dir) {
    if (isAnswered) return;
    setDraft(d => moveItem(d, i, i + dir));
  }

  function submit() {
    if (isAnswered) return;
    onSubmit(buildStudentAnswer(question, [...draft]));
  }

  return (
    <ItemFrame question={question} state={state} isAnswered={isAnswered}>
      <Instruction>
        {question.orderPrompt ? `Order: ${question.orderPrompt}` : 'Put these in the correct order'}
      </Instruction>
      {!isAnswered && (
        <p style={{ fontSize: 13, color: T.textMid, margin: "0 0 10px" }}>
          Drag the ⠿ handle, use the arrows, or tap one item and then tap the place it should go.
        </p>
      )}
      <ol aria-label="Items to order" style={{ listStyle: "none", padding: 0, margin: "0 0 20px", display: "flex", flexDirection: "column", gap: 10 }}>
        {list.map((text, i) => {
          let status = picked === text ? 'selected' : null;
          if (isAnswered) status = marks[i] ? 'right' : 'wrong';
          const c = partColors(status);
          const over = drag && drag.over === `pos:${i}` && drag.id !== text;
          return (
            <li
              key={text}
              data-drop-key={`pos:${i}`}
              data-testid={`order-row-${i}`}
              data-status={status || 'none'}
              style={{
                background: over ? T.accentLo : c.bg, border: `1.5px solid ${over ? T.accent : c.border}`, borderRadius: 12,
                padding: "8px 10px", display: "flex", alignItems: "center", gap: 10, transition: "background .15s, border-color .15s",
                opacity: drag && drag.id === text ? 0.5 : 1,
              }}
            >
              <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 8, background: T.surface, border: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: c.color, flexShrink: 0 }}>
                {isAnswered ? (marks[i] ? '✓' : '✗') : i + 1}
              </span>
              {!isAnswered && (
                <span
                  aria-hidden="true"
                  data-testid={`order-handle-${i}`}
                  onPointerDown={e => start(text, text, e)}
                  style={{ cursor: "grab", touchAction: "none", userSelect: "none", fontSize: 18, color: T.textDim, padding: "4px 6px", flexShrink: 0 }}
                >⠿</span>
              )}
              <button
                type="button"
                onClick={() => tapRow(text)}
                aria-pressed={!isAnswered ? picked === text : undefined}
                aria-label={isAnswered ? `Position ${i + 1}: ${text}` : picked && picked !== text ? `Move "${picked}" to position ${i + 1}` : `Position ${i + 1}: ${text}. Tap to pick up`}
                disabled={isAnswered}
                style={{
                  flex: 1, background: "none", border: "none", textAlign: "left", padding: "6px 0",
                  fontSize: 15, color: c.color, fontWeight: status ? 600 : 400, lineHeight: 1.5,
                  cursor: isAnswered ? "default" : "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
                  touchAction: "manipulation",
                }}
              >{text}</button>
              {!isAnswered && (
                <>
                  <button type="button" aria-label={`Move "${text}" up`} disabled={i === 0} onClick={() => nudge(i, -1)} style={smallBtn(i === 0)}>↑</button>
                  <button type="button" aria-label={`Move "${text}" down`} disabled={i === list.length - 1} onClick={() => nudge(i, 1)} style={smallBtn(i === list.length - 1)}>↓</button>
                </>
              )}
            </li>
          );
        })}
      </ol>
      <DragGhost drag={drag} />
      {!isAnswered && <CheckButton mode={state.mode} disabled={false} onClick={submit} />}
      <ResultCard
        question={question} mode={state.mode} isAnswered={isAnswered} isCorrect={isCorrect}
        showExplanation={showExplanation} feedback={isAnswered && !isCorrect ? feedbackFor(question, list) : null}
        onAskLumi={onAskLumi} onExplainVideo={onExplainVideo}
      />
    </ItemFrame>
  );
}
